import { ExternalLinkIcon } from 'lucide-react'

import { ButtonGroup, Modal, TextLink } from 'slab'

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
}

const IRS_DOCUMENTATION_LINK = 'https://www.irs.gov/affordable-care-act/employers/determining-if-an-employer-is-an-applicable-large-employer'

export default function ExplanationModal ({ open, onOpenChange }: Props) {
  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
    >
      <Modal.Body>
        <Modal.Title size='large'>Why are we asking for this?</Modal.Title>
        <Modal.Description>
          <div className='flex flex-col gap-4 body-sm mb-8'>
            <p>
              If you have 50 or more full-time equivalent employees, we'll need to collect everyone's salary information to ensure that your allowance strategy meets the Affordable Care Act (ACA)’s affordability rules.
            </p>
            <p>
              For 2026, that means an employee should pay no more than 9.96% of their household income towards their monthly insurance premium.
            </p>
          </div>
        </Modal.Description>
        <Modal.Title size='large' dismissible={false}>What qualifies as a full-time equivalent employee?</Modal.Title>
        <Modal.Description>
          <div className='flex flex-col gap-4'>
            <div className='flex flex-col gap-2'>
              <p className='body-bold'>
                An employee is considered <span className='italic'>full-time</span> if they work:
              </p>
              <ul className='body-sm list-disc list-outside pl-4'>
                <li>About 30 hours or more per week, or</li>
                <li>130 hours or more in a month.</li>
              </ul>
            </div>
            <div className='flex flex-col gap-2'>
              <p className='body-bold'>
                Full-time equivalent employees (FTEs)
              </p>
              <p className='body-sm'>
                Sometimes, several part-time employees together count as being equivalent to one full-time employee. Here's how that works:
              </p>
              <ol className='body-sm list-decimal list-outside pl-4'>
                <li>Add up the hours of all your part-time employees (but don't count more than 120 hours for any one person).</li>
                <li>Divide that number by 120. The result is how many "full-time equivalents" you have.</li>
              </ol>
            </div>
            <span className='body-sm'>
              Learn more at the <TextLink href={IRS_DOCUMENTATION_LINK} rightIcon={ExternalLinkIcon} target='_blank' rel='noopener noreferrer'>official IRS documentation</TextLink>
            </span>
          </div>
        </Modal.Description>
      </Modal.Body>
      <Modal.Footer>
        <ButtonGroup align='right'>
          <Modal.Button variant='primary' size='large'>
            Close
          </Modal.Button>
        </ButtonGroup>
      </Modal.Footer>
    </Modal>
  )
}
